import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { 
  ShieldCheck, 
  FileText, 
  Clock, 
  UserCheck, 
  Stethoscope,
  Lock,
  Download,
  Database,
  Scale
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Standard attestation text for surgeon authorization subset
export const SURGEON_ATTESTATION_TEXT = 
  "I attest that the selected procedures represent clinically possible intra-operative findings for this case.";

interface AuthorizationIntentPanelProps {
  // Primary procedure info
  primaryCpt: string;
  primaryCptDescription?: string;
  
  // Subset info
  selectedSubsetCpts: string[];
  cptDescriptions?: Record<string, string>;
  
  // Template tracking
  subsetTemplateId?: string | null;
  subsetTemplateVersion?: number | null;
  
  // Attestation info
  surgeonAttestation: boolean;
  attestationTimestamp?: string | null;
  surgeonName?: string;
  surgeonId?: string;
  
  // Authorization scope
  authorizationScope?: 'SUPERSET' | 'STANDARD' | 'SINGLE' | null;
  authorizedCpts?: string[];
  
  // Case info
  caseNumber?: string;
  createdAt?: string;
  
  // Actions
  onExportPdf?: () => void;
  isExportLoading?: boolean;
}

export function AuthorizationIntentPanel({
  primaryCpt,
  primaryCptDescription,
  selectedSubsetCpts,
  cptDescriptions = {},
  subsetTemplateId,
  subsetTemplateVersion,
  surgeonAttestation,
  attestationTimestamp,
  surgeonName,
  surgeonId,
  authorizationScope,
  authorizedCpts = [],
  caseNumber,
  createdAt,
  onExportPdf,
  isExportLoading = false,
}: AuthorizationIntentPanelProps) {
  // Format timestamp for display
  const formatTimestamp = (ts: string | null | undefined) => {
    if (!ts) return 'Not recorded';
    try {
      return format(new Date(ts), 'MMM d, yyyy h:mm:ss a');
    } catch {
      return ts;
    }
  };

  // Get scope badge color and label
  const getScopeBadge = () => {
    switch (authorizationScope) {
      case 'SUPERSET':
        return { color: 'bg-success text-success-foreground', label: 'Superset Protected' };
      case 'STANDARD':
        return { color: 'bg-primary text-primary-foreground', label: 'Standard' };
      case 'SINGLE':
        return { color: 'bg-muted text-muted-foreground', label: 'Single Procedure' };
      default:
        return { color: 'bg-muted text-muted-foreground', label: 'Not Specified' };
    }
  };

  const scopeBadge = getScopeBadge();

  return (
    <Card className="border-2 border-primary/20 bg-card">
      {/* Immutable Header */}
      <CardHeader className="pb-3 bg-primary/5 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Scale className="w-5 h-5 text-primary" />
            Authorization Intent & Attestation
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Immutable Record
            </Badge>
            {onExportPdf && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={onExportPdf}
                disabled={isExportLoading}
                className="h-7 text-xs"
              >
                <Download className="w-3 h-3 mr-1" />
                Export
              </Button>
            )}
          </div>
        </div>
        {caseNumber && (
          <p className="text-xs text-muted-foreground mt-1">
            Case: {caseNumber} • Created: {formatTimestamp(createdAt)}
          </p>
        )}
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Authorization Scope */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck className="w-4 h-4" />
            Authorization Scope
          </div>
          <Badge className={cn("font-medium", scopeBadge.color)}>
            {scopeBadge.label}
          </Badge>
        </div>

        <Separator />

        {/* Primary CPT */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Stethoscope className="w-4 h-4 text-primary" />
            Primary Procedure
          </div>
          <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-mono font-bold text-lg">{primaryCpt}</span>
                {primaryCptDescription && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {primaryCptDescription}
                  </p>
                )}
              </div>
              <Badge variant="default" className="bg-primary">Primary</Badge>
            </div>
          </div>
        </div>

        {/* Selected Subset CPTs */}
        {selectedSubsetCpts.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileText className="w-4 h-4 text-muted-foreground" />
                Authorization Subset ({selectedSubsetCpts.length} procedures)
              </div>
              {subsetTemplateId && (
                <Badge variant="outline" className="text-xs flex items-center gap-1">
                  <Database className="w-3 h-3" />
                  Template v{subsetTemplateVersion || 1}
                </Badge>
              )}
            </div>
            <div className="space-y-1">
              {selectedSubsetCpts.map((cpt) => (
                <div 
                  key={cpt}
                  className="bg-muted/50 rounded-lg p-2 border flex items-center justify-between"
                >
                  <div>
                    <span className="font-mono font-medium text-sm">{cpt}</span>
                    {cptDescriptions[cpt] && (
                      <span className="text-sm text-muted-foreground ml-2">
                        — {cptDescriptions[cpt]}
                      </span>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-xs">Subset</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Total Authorized CPTs */}
        {authorizedCpts.length > 0 && (
          <div className="bg-muted/30 rounded-lg p-3 border">
            <p className="text-xs text-muted-foreground mb-2">
              Total Authorized CPT Codes ({authorizedCpts.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {authorizedCpts.map((cpt, idx) => (
                <Badge 
                  key={cpt} 
                  variant={idx === 0 ? "default" : "secondary"}
                  className="font-mono text-xs"
                >
                  {cpt}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <Separator />

        {/* Surgeon Attestation */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <UserCheck className="w-4 h-4 text-success" />
            Surgeon Attestation
          </div>
          
          {/* Attestation Status */}
          <div className={cn(
            "rounded-lg p-3 border",
            surgeonAttestation 
              ? "bg-success/5 border-success/30" 
              : "bg-destructive/5 border-destructive/30"
          )}>
            <div className="flex items-start gap-3">
              <div className={cn(
                "w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                surgeonAttestation ? "bg-success" : "bg-destructive"
              )}>
                {surgeonAttestation ? (
                  <ShieldCheck className="w-3 h-3 text-success-foreground" />
                ) : (
                  <span className="text-destructive-foreground text-xs">✕</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "text-sm font-medium",
                  surgeonAttestation ? "text-success" : "text-destructive"
                )}>
                  {surgeonAttestation ? 'Attestation Confirmed' : 'Attestation Not Completed'}
                </p>
                <p className="text-sm text-muted-foreground mt-1 italic">
                  "{SURGEON_ATTESTATION_TEXT}"
                </p>
              </div>
            </div>
          </div>

          {/* Attestation Details */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Timestamp
              </p>
              <p className="font-mono text-xs bg-muted/50 rounded px-2 py-1">
                {formatTimestamp(attestationTimestamp)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <UserCheck className="w-3 h-3" />
                Attesting Surgeon
              </p>
              <p className="text-xs bg-muted/50 rounded px-2 py-1 truncate">
                {surgeonName || surgeonId || 'Not recorded'}
              </p>
            </div>
          </div>
        </div>

        {/* Template Tracking (for audit) */}
        {subsetTemplateId && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Template Reference</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-muted/30 rounded p-2">
                  <p className="text-muted-foreground">Template ID</p>
                  <p className="font-mono truncate">{subsetTemplateId}</p>
                </div>
                <div className="bg-muted/30 rounded p-2">
                  <p className="text-muted-foreground">Version</p>
                  <p className="font-mono">{subsetTemplateVersion || 'N/A'}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Compliance Footer */}
        <div className="bg-muted/20 rounded-lg p-3 border border-dashed">
          <p className="text-xs text-muted-foreground text-center">
            This record is immutable and represents the surgeon's documented clinical intent 
            at the time of authorization request submission. Any modifications create a new 
            audit trail entry.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
